/**
 * `/admin/automation/quota/:account` (A7Y-3802) — everything about one coding
 * account that the overview table deliberately leaves out: the role in prose
 * rather than a tooltip, full-width windows with their reset times, the
 * capacity block, the reserved-capacity callout with the comparator's own
 * policy string, and the refresh-failure message.
 *
 * NO NEW REQUEST. It selects its account out of the same cached
 * `['autobuild-quota']` / `['autobuild-leases']` queries the overview owns, so
 * it inherits that 30s polling rather than adding a second fetch.
 *
 * An unknown `:account` renders a not-found state. It is checked against the
 * accounts the API actually returned, not against a hand-kept key list — the
 * registry is the API's to state, and a literal here would be the stale-literal
 * class A7Y-3787 removed from this directory.
 */
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft } from 'lucide-react';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { CapacityConcepts } from './CapacityConcepts';
import { ProviderIcon, QuotaBar, RoleBadge, ScopedLimits } from './quota-account-parts';
import {
  ROLE_META,
  capacityUnavailableReason,
  hasCachedValues,
  sourceLabel,
} from './quota-account-meta';
import { fmtLocal, useNow } from './shared';

const BACK_LINK = (
  <Link
    to="/admin/automation"
    className="mb-4 inline-flex items-center text-sm text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
  >
    <ArrowLeft className="mr-1 h-4 w-4" /> All accounts
  </Link>
);

export default function QuotaAccountDetailPage() {
  const { account: accountKey } = useParams<{ account: string }>();
  const now = useNow(60_000);

  const quota = useQuery({
    queryKey: ['autobuild-quota'],
    queryFn: () => api.getAiQuotaStatus(),
    refetchInterval: 30_000,
    refetchOnWindowFocus: false,
  });
  const leases = useQuery({
    queryKey: ['autobuild-leases'],
    queryFn: () => api.getAutomationLeaseStatus(),
    refetchInterval: 30_000,
    refetchOnWindowFocus: false,
  });

  const account = quota.data?.accounts.find((candidate) => candidate.account === accountKey);

  if (quota.isError) {
    return (
      <div className="space-y-4">
        {BACK_LINK}
        <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          Quota status failed to load. Check the Worker logs for /admin/autobuild/quota.
        </div>
      </div>
    );
  }

  // Loading is checked before not-found: an account that simply has not arrived
  // yet must not be reported as one that does not exist.
  if (!quota.data) {
    return (
      <div className="space-y-4">
        {BACK_LINK}
        <div className="p-6 text-sm text-zinc-500">Reading account quota…</div>
      </div>
    );
  }

  if (!account) {
    return (
      <div className="space-y-4">
        {BACK_LINK}
        <PageHeader title="Unknown account" subtitle={`No coding account named “${accountKey}”.`} />
        <div className="rounded-lg border border-zinc-200 p-6 text-sm text-zinc-500 dark:border-zinc-800">
          The quota endpoint returned no account with this key. It may have been retired from the
          account registry, or the link may be stale.
        </div>
      </div>
    );
  }

  const cached = hasCachedValues(account);
  const displayedAt = cached ? account.lastSuccessfulAt : account.updatedAt;
  const source = cached ? sourceLabel(account) : null;
  const roleMeta = account.role ? ROLE_META[account.role] : undefined;
  const capacity = leases.data?.leases.find((lease) => lease.account === account.account)?.capacity;
  const unavailableReason = capacityUnavailableReason(
    leases.isError,
    leases.isLoading,
    Boolean(leases.data)
  );

  return (
    <div className="space-y-4">
      <div>
        {BACK_LINK}
        <PageHeader
          title={account.email ?? account.label}
          subtitle={
            displayedAt
              ? `${cached ? 'Updated' : 'Last attempt'} ${fmtLocal(displayedAt)}${source ? ` via ${source}` : ''}`
              : 'This account has never reported a quota reading.'
          }
        >
          <div className="flex shrink-0 items-center gap-2">
            <ProviderIcon provider={account.provider} />
            <RoleBadge role={account.role} />
          </div>
        </PageHeader>
      </div>

      {/* The role explanation is prose here rather than a tooltip — the detail
          page is where an operator comes to find out why an account was, or was
          not, picked to build. */}
      {roleMeta ? (
        <div className="rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
          <div className="text-[10px] uppercase tracking-wide text-zinc-400">Role</div>
          <div className="mt-1 text-sm font-semibold text-zinc-900 dark:text-zinc-100">
            {roleMeta.label}
          </div>
          <p className="mt-1 text-xs leading-relaxed text-zinc-600 dark:text-zinc-400">
            {roleMeta.title}
          </p>
        </div>
      ) : null}

      <div className="rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
        {cached ? (
          <div className="space-y-4">
            <QuotaBar
              label="Session"
              value={account.session}
              thresholds={account.thresholds}
              now={now}
              reset="full"
            />
            <QuotaBar
              label="Weekly"
              value={account.weekly}
              thresholds={account.thresholds}
              now={now}
              reset="full"
            />
            {/* Per-model weekly limits, under the account-wide weekly bar. A
                model at 100% is unavailable on this account until the window
                cycles; the account itself keeps running everything else, which
                is why this never modifies the figure above. */}
            {account.scopedLimits && account.scopedLimits.length > 0 ? (
              <div className="space-y-1">
                <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
                  Per-model weekly
                </span>
                <ScopedLimits limits={account.scopedLimits} now={now} />
              </div>
            ) : null}
          </div>
        ) : (
          <div className="flex min-h-24 items-center justify-center rounded-md bg-zinc-50 px-3 text-center text-xs text-zinc-500 dark:bg-zinc-950/50">
            Awaiting the first quota refresh.
          </div>
        )}

        {/* The capacity concepts, kept apart. The bars above say how much of
            each window is SPENT; this block says what may be spent, by whom,
            and which of the three refuses the next request (A7Y-2821 epic §7). */}
        <div className="mt-4 border-t border-zinc-100 pt-3 dark:border-zinc-800">
          {capacity ? (
            <CapacityConcepts capacity={capacity} />
          ) : (
            <div className="text-[11px] leading-relaxed text-zinc-400">
              {unavailableReason ?? 'Capacity has not loaded yet.'}
            </div>
          )}
        </div>
      </div>

      {account.error ? (
        <div className="flex items-start gap-1.5 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            Latest refresh failed: {account.error}
            {cached ? ' Showing the last good snapshot.' : ''}
          </span>
        </div>
      ) : null}
    </div>
  );
}
